import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { loginClinic } from '../lib/api';
import { Helmet } from 'react-helmet-async';

const ClinicLoginPage = () => {
  const navigate = useNavigate();

  // Redirect already-authenticated clinic admins to portal
  useEffect(() => {
    if (
      localStorage.getItem('vc_clinic_access_token') &&
      localStorage.getItem('vc_clinic_account')
    ) {
      navigate('/clinic-portal', { replace: true });
    }
  }, [navigate]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const { data } = await loginClinic(email, password);
      const { account, accessToken, refreshToken } = data.data;

      // Store clinic-specific keys (separate from patient auth)
      localStorage.setItem('vc_clinic_access_token', accessToken);
      localStorage.setItem('vc_clinic_refresh_token', refreshToken);
      localStorage.setItem('vc_clinic_account', JSON.stringify(account));

      toast.success(`Welcome back, ${account.name}!`);
      window.dispatchEvent(new Event('clinicAuthChange'));
      navigate('/clinic-portal');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Clinic Partner Login — VietClinics</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Header />

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1FA7A1]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-[#1FA7A1]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Clinic Staff Login</h1>
            <p className="text-gray-600">Sign in to manage your clinic and bookings</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="clinic@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 h-12 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="clinic-email-input"
                    autoFocus
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pl-10 pr-10 h-12 ${errors.password ? 'border-red-500' : ''}`}
                    data-testid="clinic-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] h-12 text-base font-semibold"
                data-testid="clinic-login-btn"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In to Portal
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Trust indicator */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Shield className="h-4 w-4" />
            <span>Your data is secure and encrypted</span>
          </div>

          {/* Footer links */}
          <div className="mt-6 space-y-2 text-center text-sm">
            <p className="text-gray-600">
              Not registered yet?{' '}
              <Link
                to="/clinic-registration"
                className="text-[#1FA7A1] font-medium hover:underline"
                data-testid="register-clinic-link"
              >
                Register your clinic
              </Link>
            </p>
            <p className="text-gray-500">
              Are you a patient?{' '}
              <Link
                to="/login"
                className="text-[#1FA7A1] font-medium hover:underline"
                data-testid="patient-login-link"
              >
                Sign in as a patient
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ClinicLoginPage;
